import React from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core';
import cloneDeep from 'lodash.clonedeep';

export const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

const useStyles = makeStyles(() => ({
    reccContent: {
        fontFamily: 'Nimbus Sans Bold',
        fontSize: '16px',
        lineHeight: '24px'
    },
    reccSubContent: {
        fontFamily: 'Nimbus Sans Light',
        lineHeight: '24px',
        fontSize: '16px'
    },
    tableHeader: {
        color: 'white',
        fontFamily: 'Nimbus Sans Bold',
        fontSize: '14px',
    },
    cellLeft: {
        padding: '16px 0 0 16px',
        verticalAlign: 'top'
    },
    cellMiddle: {
        padding: '16px 24px 16px 16px',
        verticalAlign: 'top'
    },
    cellRight: {
        padding: '16px 16px 12px 0',
        verticalAlign: 'top'
    },
}));

function Row(props) {
    const { row, total, rowTotal, percent, word } = props;
    const classes = useStyles();

    const calcRowData = row;
    calcRowData.forEach((subRow, i) => {
        const subRowTotal = Number((subRow.allocationPercentage / 100 * total).toFixed(2));
        calcRowData[i].roundedTotal = subRowTotal;
    });
    const roundedTotals = calcRowData.reduce((agg, ro) => agg + ro.roundedTotal, 0);
    const totalMismatch = Number((rowTotal - roundedTotals).toFixed(2));
    if (totalMismatch !== 0) {
        calcRowData[calcRowData.length - 1].roundedTotal = Number((calcRowData[calcRowData.length - 1].roundedTotal + totalMismatch).toFixed(2));
    }

    return !row || (row && !row.length) ? null : (
        <>
            <TableRow style={{ backgroundColor: '#F4F5F6' }} id={`recc-port-${word}-row`}>
                <TableCell style={{ width: '30%'}} align="left" className={classes.cellLeft} id={`${word}-class-cell`}>
                    <Typography className={classes.reccContent} id={`${word}-class-header`}>
                        {word}
                    </Typography>
                </TableCell>
                <TableCell style={{ width: '40%'}} />
                <TableCell style={{ width: '30%'}} align="right" className={classes.cellRight} id={`${word}-amt-cell`}>
                    <Typography className={classes.reccContent} style={{ paddingBottom: '4px' }} id={`${word}-amt`}>
                        {formatter.format(rowTotal)}
                    </Typography>
                    <Typography className={classes.reccContent} id={`${word}-weight`}>
                        {percent.toFixed(1)}%
                    </Typography>
                </TableCell>
            </TableRow>
            {calcRowData.map((modelAllocation, index) => (
                <TableRow
                    key={`${modelAllocation.assetClassCategoryId}_${modelAllocation.subAssetClassCategoryId}_${modelAllocation.subSubAssetClassCategoryId}`}
                    id={`sub-class-${word}-row`}
                >
                    <TableCell style={{ paddingLeft: 16, width: '30%' }} className={classes.cellLeft} id={`sub-class-${word}-asset-class-cell-${index}`}>
                        <Typography className={classes.reccSubContent} id={`sub-class-${word}-asset-class-${index}`}>
                            {modelAllocation.displayName}
                        </Typography>
                    </TableCell>
                    <TableCell style={{ width: '40%'}} className={classes.cellMiddle} id={`sub-class-${word}-fund-cell-${index}`}>
                        {modelAllocation.funds.sort((a, b) => a.fundTypeId - b.fundTypeId).map((fund, idx, coll) => (
                            <Typography
                                className={classes.reccSubContent}
                                id={`sub-class-${word}-fund-${index}-${idx}`}
                                key={`sub-class-${word}-fund-${modelAllocation.subSubAssetClassCategoryId}-${fund.fundName}`}
                            >
                                {idx === coll.length - 1 ? fund.fundName : `${fund.fundName} +`}
                            </Typography>
                        ))}
                    </TableCell>
                    <TableCell style={{ width: '30%'}} align="right" className={classes.cellRight} id={`sub-class-${word}-amt-cell-${index}`}>
                        <Typography className={classes.reccSubContent} id={`sub-class-${word}-amt-${index}`}>
                            {formatter.format(modelAllocation.roundedTotal)}
                        </Typography>
                        <Typography className={classes.reccSubContent} id={`sub-class-${word}-weight-${index}`}>
                            {modelAllocation.allocationPercentage.toFixed(1)}%
                        </Typography>
                    </TableCell>
                </TableRow>
            ))}
        </>
    );
}
/* eslint-disable no-nested-ternary */
const RecommendedPortfolioTable = ({ modelAssetAllocationAssociatedFund, total }) => {
    const classes = useStyles();
    const modelDataCopy = cloneDeep(modelAssetAllocationAssociatedFund);

    const equities = modelDataCopy.modelAllocations.filter(assetAllocation => assetAllocation.assetClassCategoryId === 5257);
    const bonds = modelDataCopy.modelAllocations.filter(assetAllocation => assetAllocation.assetClassCategoryId === 5258);
    const cash = modelDataCopy.modelAllocations.filter(assetAllocation => assetAllocation.assetClassCategoryId === 5259);

    const equitiesPercent = equities.reduce((agg, ro) => agg + ro.allocationPercentage, 0);
    const bondsPercent = bonds.reduce((agg, ro) => agg + ro.allocationPercentage, 0);
    const cashPercent = cash.reduce((agg, ro) => agg + ro.allocationPercentage, 0);

    const equitiesTotal = Number((total / 100 * equitiesPercent).toFixed(2));
    const bondsTotal = Number((total / 100 * bondsPercent).toFixed(2));
    const cashTotal = Number((total / 100 * cashPercent).toFixed(2));

    const totalMismatch = Number((Number(total) - (equitiesTotal + bondsTotal + cashTotal)).toFixed(2));
    const adjustedCashTotal = Number((cashTotal + totalMismatch).toFixed(2));

    return (
        <TableContainer component={Paper} id='recc-model-table-container'>
            <Table aria-label="collapsible table" id='recc-model-table'>
                <TableHead id='recc-model-table-head'>
                    <TableRow style={{ backgroundColor: '#1E2D39' }} id='recc-model-table-header-row'>
                        <TableCell align="left" id='asset-class-header-cell' style={{ width: '30%'}}>
                            <Typography className={classes.tableHeader} id='asset-class-header'>
                                ASSET CLASS
                        </Typography>
                        </TableCell>
                        <TableCell align="left" id='fund-header-cell' style={{ width: '40%'}}>
                            <Typography className={classes.tableHeader} id='fund-header'>
                                FUND(S)
                        </Typography>
                        </TableCell>
                        <TableCell align="right" id='amt-header-cell' style={{ width: '30%'}}>
                            <Typography className={classes.tableHeader} id='amt-header'>
                                AMOUNT / WEIGHT
                        </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody id='recc-port-table-body'>
                    <Row
                        key='Equities'
                        word='Equities'
                        total={total}
                        percent={equitiesPercent}
                        rowTotal={equitiesTotal}
                        row={equities}
                    />
                    <Row
                        key='Bonds'
                        word='Bonds'
                        total={total}
                        percent={bondsPercent}
                        rowTotal={bondsTotal}
                        row={bonds}
                    />
                    <Row
                        key='Cash'
                        word='Cash'
                        total={total}
                        percent={cashPercent}
                        rowTotal={adjustedCashTotal}
                        row={cash}
                    />
                </TableBody>
            </Table>
        </TableContainer>
    )
};

export default RecommendedPortfolioTable;
